import { useEffect } from 'react'

/** Focus targets where a key keeps its usual meaning instead of acting on the preview. */
const TEXT_FIELDS = 'input, textarea, select, [contenteditable="true"]'
const CONTROLS = `${TEXT_FIELDS}, button, a[href], [role="button"], [role="radio"], [role="tab"]`

/**
 * While a layout preview is open, Enter applies it and Escape cancels it — unless focus is in a
 * text field, where both keys keep their usual meaning, or Enter is pressed on a control (a button,
 * link, tab or option), which it activates instead.
 */
export function useLayoutPreviewKeys(
  active: boolean,
  { onApply, onCancel }: { onApply: () => void; onCancel: () => void }
): void {
  useEffect(() => {
    if (!active) {
      return
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      const target = event.target as HTMLElement | null
      if (target?.closest(TEXT_FIELDS)) {
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        onCancel()
      } else if (event.key === 'Enter' && !target?.closest(CONTROLS)) {
        event.preventDefault()
        onApply()
      }
    }
    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [active, onApply, onCancel])
}
